import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Autocomplete,
  Paper,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import { congesAPI, joursFeriesAPI } from '../../../services/api';
import {
  Conge,
  CongeCreate,
  TYPE_CONGE_OPTIONS,
  TypeConge,
  EmployeeSoldeResponse,
} from '../../../types/conge';
import { EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import {
  SlideUpTransition,
  useMinimizable,
  DialogControls,
  MinimizedBar,
} from '../../../components/MinimizableDialog';

const HR_COLOR = '#00897B';

interface CongeFormDialogProps {
  open: boolean;
  onClose: () => void;
  conge: Conge | null;
  employees: EmployeeDropdownItem[];
  onSuccess: () => void;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const CongeFormDialog: React.FC<CongeFormDialogProps> = ({
  open,
  onClose,
  conge,
  employees,
  onSuccess,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDropdownItem | null>(null);
  const [typeConge, setTypeConge] = useState<TypeConge>('CP');
  const [dateDebut, setDateDebut] = useState<string>('');
  const [dateFin, setDateFin] = useState<string>('');
  const [motif, setMotif] = useState('');
  const [loading, setLoading] = useState(false);
  const [soldeInfo, setSoldeInfo] = useState<EmployeeSoldeResponse | null>(null);
  const [loadingSolde, setLoadingSolde] = useState(false);
  const [holidays, setHolidays] = useState<Set<string>>(new Set());
  const [calculatedDays, setCalculatedDays] = useState<number>(0);

  const { showSuccess, showError } = useAlert();

  const isEditing = !!conge;

  // Draft caching — only for new congés (never when editing an existing row).
  // Derived state (soldeInfo / holidays / calculatedDays) is recomputed by the
  // effects from the cached inputs, so it is intentionally not stored.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    selectedEmployee: EmployeeDropdownItem | null;
    typeConge: TypeConge;
    dateDebut: string;
    dateFin: string;
    motif: string;
  }>('conge', !isEditing);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (conge) {
        // Editing mode
        const emp = employees.find(e => e.id === conge.employee_id);
        setSelectedEmployee(emp || null);
        setTypeConge(conge.type_conge);
        setDateDebut(conge.date_debut);
        setDateFin(conge.date_fin);
        setMotif(conge.motif || '');
      } else {
        // New mode — repopulate from a draft if one was stashed by an accidental
        // backdrop/Escape close, otherwise do the blank reset.
        const draft = loadDraft();
        if (draft) {
          setSelectedEmployee(draft.selectedEmployee);
          setTypeConge(draft.typeConge);
          setDateDebut(draft.dateDebut);
          setDateFin(draft.dateFin);
          setMotif(draft.motif);
        } else {
          setSelectedEmployee(null);
          setTypeConge('CP');
          setDateDebut('');
          setDateFin('');
          setMotif('');
        }
        setSoldeInfo(null);
        setHolidays(new Set());
        setCalculatedDays(0);
      }
    }
  }, [open, conge, employees, loadDraft]);

  // Fetch employee balance when employee is selected (totals across all years)
  useEffect(() => {
    const fetchSolde = async () => {
      if (!selectedEmployee) {
        setSoldeInfo(null);
        return;
      }

      setLoadingSolde(true);
      try {
        const data = await congesAPI.getEmployeeSolde(selectedEmployee.id);
        setSoldeInfo(data);
      } catch (error) {
        console.error('Error fetching solde:', error);
        setSoldeInfo(null);
      } finally {
        setLoadingSolde(false);
      }
    };

    fetchSolde();
  }, [selectedEmployee]);

  // Fetch holidays and calculate leave days when dates change
  useEffect(() => {
    const calculateLeaveDays = async () => {
      if (!dateDebut || !dateFin || dateFin < dateDebut) {
        setCalculatedDays(0);
        setHolidays(new Set());
        return;
      }

      try {
        // Fetch holidays between the selected dates
        const holidaysList = await joursFeriesAPI.getBetween(dateDebut, dateFin);
        const holidayDates = new Set(holidaysList.map(h => h.date));
        setHolidays(holidayDates);

        // Calculate leave days (exclude only Sunday and holidays)
        let days = 0;
        const current = new Date(dateDebut);
        const end = new Date(dateFin);

        while (current <= end) {
          const dayOfWeek = current.getDay(); // 0=Sunday, 6=Saturday
          const dateStr = current.toISOString().split('T')[0];

          // Skip Sunday only (0 = Sunday is rest day)
          // Saturday (6) is a working day
          if (dayOfWeek === 0) {
            current.setDate(current.getDate() + 1);
            continue;
          }

          // Skip holidays
          if (holidayDates.has(dateStr)) {
            current.setDate(current.getDate() + 1);
            continue;
          }

          days++;
          current.setDate(current.getDate() + 1);
        }

        setCalculatedDays(days);
      } catch (error) {
        console.error('Error fetching holidays:', error);
        // Fallback: calculate without holidays
        let days = 0;
        const current = new Date(dateDebut);
        const end = new Date(dateFin);
        while (current <= end) {
          if (current.getDay() !== 0) { // Skip Sunday only
            days++;
          }
          current.setDate(current.getDate() + 1);
        }
        setCalculatedDays(days);
      }
    };

    calculateLeaveDays();
  }, [dateDebut, dateFin]);

  const estimatedDays = calculatedDays;

  // Calculate months of service for 6-month rule (Article 231 Code du Travail)
  const getMonthsOfService = (): number => {
    if (!soldeInfo?.employee?.date_embauche) return 0;
    const hireDate = new Date(soldeInfo.employee.date_embauche);
    const today = new Date();
    const years = today.getFullYear() - hireDate.getFullYear();
    const months = today.getMonth() - hireDate.getMonth();
    return years * 12 + months;
  };

  const monthsOfService = getMonthsOfService();
  const hasMinimumSeniority = monthsOfService >= 6;
  const showSeniorityWarning = typeConge === 'CP' && selectedEmployee && soldeInfo && !hasMinimumSeniority;

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      showError('Veuillez sélectionner un employé');
      return;
    }
    if (!dateDebut || !dateFin) {
      showError('Veuillez sélectionner les dates de début et de fin');
      return;
    }
    if (dateFin < dateDebut) {
      showError('La date de fin doit être postérieure à la date de début');
      return;
    }

    // Check 6-month seniority rule for CP (Article 231 Code du Travail)
    if (typeConge === 'CP' && !hasMinimumSeniority) {
      showError(`L'employé doit avoir au moins 6 mois d'ancienneté pour bénéficier du congé payé (Article 231 du Code du Travail). Ancienneté actuelle: ${monthsOfService} mois.`);
      return;
    }

    // Check balance for CP
    if (typeConge === 'CP' && soldeInfo && soldeInfo.soldes.length > 0) {
      if (estimatedDays > soldeInfo.total_jours_restants) {
        showError(`Solde insuffisant. Jours restants: ${soldeInfo.total_jours_restants}, Demandés: ${estimatedDays}`);
        return;
      }
    }

    setLoading(true);
    try {
      const data: CongeCreate = {
        employee_id: selectedEmployee.id,
        type_conge: typeConge,
        date_debut: dateDebut,
        date_fin: dateFin,
        motif: motif || undefined,
      };

      if (isEditing && conge) {
        await congesAPI.update(conge.id, data);
        showSuccess('Demande de congé modifiée avec succès');
      } else {
        await congesAPI.create(data);
        showSuccess('Demande de congé créée avec succès');
      }

      clearDraft();
      onSuccess();
      onClose();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Annuler = deliberate cancel: discard any draft, then close.
  const handleClose = () => {
    clearDraft();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (!isEditing) {
        saveDraft({ selectedEmployee, typeConge, dateDebut, dateFin, motif });
      }
      onClose();
    }
  };

  return (
    <>
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={SlideUpTransition}
    >
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {isEditing ? 'Modifier la Demande de Congé' : 'Nouvelle Demande de Congé'}
          <DialogControls color="white" onMinimize={minimize} onClose={handleClose} />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Employee Selection */}
            <Grid item xs={12}>
              <Autocomplete
                value={selectedEmployee}
                onChange={(_, newValue) => setSelectedEmployee(newValue)}
                options={employees.filter(emp => emp.type_contrat !== 'STAGE')}
                getOptionLabel={(option) => `${option.matricule} - ${option.nom_complet}`}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="500">
                        {option.nom_complet}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.matricule} • {option.fonction || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Employé *" fullWidth />
                )}
                disabled={isEditing}
              />
            </Grid>

            {/* Employee Balance Info */}
            {selectedEmployee && soldeInfo && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: HR_COLOR }}>
                    Information Employé & Solde Congé
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Date d'embauche:</strong> {new Date(soldeInfo.employee.date_embauche).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Fonction:</strong> {soldeInfo.employee.fonction || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Solde Congé Payé (CP) - Total:
                      </Typography>
                      {soldeInfo.soldes.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Acquis: ${soldeInfo.total_jours_acquis}`}
                            size="small"
                            sx={{ bgcolor: '#1976D2', color: 'white' }}
                          />
                          <Chip
                            label={`Pris: ${soldeInfo.total_jours_pris}`}
                            size="small"
                            sx={{ bgcolor: '#FFA726', color: 'white' }}
                          />
                          <Chip
                            label={`Restants: ${soldeInfo.total_jours_restants}`}
                            size="small"
                            sx={{
                              bgcolor: soldeInfo.total_jours_restants <= 0 ? '#EF5350' : '#66BB6A',
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {loadingSolde ? 'Chargement...' : 'Aucune donnée disponible'}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* 6-Month Seniority Warning */}
            {showSeniorityWarning && (
              <Grid item xs={12}>
                <Alert severity="error">
                  L'employé doit avoir au moins 6 mois d'ancienneté pour bénéficier du congé payé (Article 231 du Code du Travail).
                  Ancienneté actuelle: {monthsOfService} mois.
                </Alert>
              </Grid>
            )}

            {/* Type de Congé */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type de Congé *</InputLabel>
                <Select
                  value={typeConge}
                  onChange={(e) => setTypeConge(e.target.value as TypeConge)}
                  label="Type de Congé *"
                >
                  {TYPE_CONGE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box>
                        <Typography variant="body2">{opt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {opt.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Estimated Days Display */}
            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: estimatedDays > 0 ? HR_COLOR : '#f5f5f5',
                  color: estimatedDays > 0 ? 'white' : 'text.secondary',
                }}
              >
                <Typography variant="h4">{estimatedDays}</Typography>
                <Typography variant="body2">jour(s) ouvrable(s)</Typography>
                {holidays.size > 0 && (
                  <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.8 }}>
                    ({holidays.size} jour(s) férié(s) exclu(s))
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Date Début */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date de Début *"
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Date Fin */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date de Fin *"
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: dateDebut || undefined }}
              />
            </Grid>

            {/* Motif */}
            <Grid item xs={12}>
              <TextField
                label="Motif (optionnel)"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Raison de la demande de congé..."
              />
            </Grid>

            {/* Balance Warning */}
            {typeConge === 'CP' && soldeInfo && soldeInfo.soldes.length > 0 && estimatedDays > soldeInfo.total_jours_restants && hasMinimumSeniority && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Le nombre de jours demandés ({estimatedDays}) dépasse le solde disponible ({soldeInfo.total_jours_restants} jours).
                </Alert>
              </Grid>
            )}
          </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !selectedEmployee || !dateDebut || !dateFin || !!showSeniorityWarning}
          sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
        >
          {loading ? 'Enregistrement...' : isEditing ? 'Modifier' : 'Créer la Demande'}
        </Button>
      </DialogActions>
    </Dialog>

    <MinimizedBar
      open={open && minimized}
      color="#009688"
      title={isEditing ? 'Modifier la Demande de Congé' : 'Nouvelle Demande de Congé'}
      onRestore={restore}
      onClose={handleClose}
    />
    </>
  );
};

export default CongeFormDialog;
